import { useCallback, useEffect, useRef, useState } from "react";

import { getVideos, getVideoDetails } from "../services/videoService";

const DEFAULT_LIMIT = 12;

const extractVideos = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.videos)) return data.videos;
  if (Array.isArray(data?.data?.videos)) return data.data.videos;
  return [];
};

export const useVideos = ({
  creator = "katchrugby",
  limit = DEFAULT_LIMIT,
  search = "",
  autoLoad = true,
} = {}) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const requestIdRef = useRef(0);
  const offsetRef = useRef(0);
  const fetchingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const fetchVideos = useCallback(
    async (nextOffset = 0) => {
      if (fetchingRef.current && nextOffset > 0) {
        return false;
      }

      const requestId = ++requestIdRef.current;
      fetchingRef.current = true;

      if (nextOffset === 0) {
        setLoading(true);
        setVideos([]);
        setHasMore(true);
        hasMoreRef.current = true;
        offsetRef.current = 0;
      } else {
        setLoadingMore(true);
      }

      setError("");

      try {
        const response = await getVideos({
          creator,
          search,
          limit,
          offset: nextOffset,
        });

        if (requestId !== requestIdRef.current) {
          return false;
        }

        if (response?.success === false) {
          throw new Error(response.message || "Unable to load videos.");
        }

        const items = extractVideos(response);

        const pagination =
          response?.pagination || response?.data?.pagination || {};

        const nextOffsetValue = nextOffset + items.length;

        const nextHasMore =
          typeof pagination.hasMore === "boolean"
            ? pagination.hasMore
            : items.length >= limit;

        setVideos((previous) => {
          if (nextOffset === 0) return items;

          const knownIds = new Set(
            previous.map((video) => video.videoId ?? video.videoTag),
          );

          return [
            ...previous,
            ...items.filter(
              (video) => !knownIds.has(video.videoId ?? video.videoTag),
            ),
          ];
        });

        offsetRef.current = nextOffsetValue;
        hasMoreRef.current = nextHasMore;
        setHasMore(nextHasMore);

        if (pagination.total !== undefined) {
          setTotal(Number(pagination.total) || 0);
        }

        return true;
      } catch (requestError) {
        if (requestId === requestIdRef.current) {
          setError(
            requestError?.response?.data?.message ||
              requestError?.message ||
              "Unable to load videos.",
          );
        }

        return false;
      } finally {
        if (requestId === requestIdRef.current) {
          fetchingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [creator, search, limit],
  );

  const refreshVideos = useCallback(() => {
    return fetchVideos(0);
  }, [fetchVideos]);

  const loadMoreVideos = useCallback(() => {
    if (fetchingRef.current || !hasMoreRef.current) {
      return Promise.resolve(false);
    }

    return fetchVideos(offsetRef.current);
  }, [fetchVideos]);

  useEffect(() => {
    if (!autoLoad) return;

    void fetchVideos(0);

    return () => {
      requestIdRef.current += 1;
      fetchingRef.current = false;
    };
  }, [autoLoad, fetchVideos]);

  return {
    videos,
    loading,
    loadingMore,
    error,
    total,
    hasMore,
    refreshVideos,
    loadMoreVideos,
  };
};

export const useVideoDetails = (videoTag) => {
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!videoTag) {
      setVideo(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      setVideo(null);

      try {
        const response = await getVideoDetails(videoTag);

        if (cancelled) return;

        if (response?.success === false) {
          throw new Error(response.message || "Unable to load video.");
        }

        setVideo(
          response?.video || response?.data?.video || response?.data || null,
        );
      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError?.response?.data?.message ||
              requestError?.message ||
              "Unable to load video.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [videoTag]);

  return { video, loading, error };
};

export default useVideos;
